import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  AlertTriangle, Shield, FileText, Calculator, Brain, Sparkles, 
  CheckCircle, XCircle, TrendingUp, TrendingDown, RefreshCw,
  Loader2, AlertCircle, DollarSign, Percent, FileCheck, Search
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AITools = () => {
  const [activeTab, setActiveTab] = useState('anomaly');
  
  // Anomaly Detection State
  const [anomalyData, setAnomalyData] = useState(null);
  const [anomalyLoading, setAnomalyLoading] = useState(false);
  
  // Smart Invoice State
  const [clientName, setClientName] = useState('');
  const [invoiceSuggestions, setInvoiceSuggestions] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  
  // Tax Optimization State
  const [taxData, setTaxData] = useState(null);
  const [taxLoading, setTaxLoading] = useState(false);

  // Fetch Anomaly Detection
  const fetchAnomalies = async () => {
    setAnomalyLoading(true);
    try {
      const response = await axios.get(`${API}/ai/anomalies`);
      setAnomalyData(response.data);
    } catch (error) {
      toast.error('Failed to detect anomalies');
    } finally {
      setAnomalyLoading(false);
    }
  };

  // Fetch Smart Invoice Suggestions
  const fetchInvoiceSuggestions = async () => {
    if (!clientName.trim()) {
      toast.error('Please enter a client name');
      return;
    }
    setInvoiceLoading(true);
    try {
      const response = await axios.post(`${API}/ai/smart-invoice`, {
        client_name: clientName
      });
      setInvoiceSuggestions(response.data);
      toast.success('Invoice suggestions generated!');
    } catch (error) {
      toast.error('Failed to generate suggestions');
    } finally {
      setInvoiceLoading(false);
    }
  };

  // Fetch Tax Optimization
  const fetchTaxOptimization = async () => {
    setTaxLoading(true);
    try {
      const response = await axios.get(`${API}/ai/tax-optimization`);
      setTaxData(response.data);
    } catch (error) {
      toast.error('Failed to get tax optimization');
    } finally {
      setTaxLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR'
    }).format(value || 0);
  };

  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'high': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'low': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-heading font-bold text-2xl sm:text-3xl text-white flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          AI Tools Suite
        </h1>
        <p className="text-muted-foreground mt-1">Powerful AI-driven financial analysis and automation</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-orbit-card border border-orbit-border p-1 grid grid-cols-3 w-full max-w-2xl">
          <TabsTrigger value="anomaly" className="data-[state=active]:bg-red-500/20 data-[state=active]:text-red-400">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Anomaly Detection
          </TabsTrigger>
          <TabsTrigger value="invoice" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
            <FileText className="w-4 h-4 mr-2" />
            Smart Invoice
          </TabsTrigger>
          <TabsTrigger value="tax" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400">
            <Calculator className="w-4 h-4 mr-2" />
            Tax Optimizer
          </TabsTrigger>
        </TabsList>

        {/* Anomaly Detection Tab */}
        <TabsContent value="anomaly" className="space-y-6">
          <Card className="bg-gradient-to-br from-red-500/10 to-orange-500/5 border-red-500/20">
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                  <CardTitle className="text-white">AI Anomaly Detection</CardTitle>
                  <p className="text-sm text-muted-foreground">Detect unusual transactions and potential fraud</p>
                </div>
              </div>
              <Button 
                onClick={fetchAnomalies}
                disabled={anomalyLoading}
                className="bg-red-500 hover:bg-red-600"
              >
                {anomalyLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Scan Now
              </Button>
            </CardHeader>
            <CardContent>
              {anomalyLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-8 h-8 text-red-400 animate-spin" />
                    <span className="text-muted-foreground">AI is scanning your transactions...</span>
                  </div>
                </div>
              ) : anomalyData ? (
                <div className="space-y-6">
                  {/* Risk Score */}
                  <div className="flex items-center gap-6">
                    <div className={`text-center p-4 rounded-xl ${
                      anomalyData.risk_score < 30 ? 'bg-emerald-500/10' :
                      anomalyData.risk_score < 60 ? 'bg-yellow-500/10' : 'bg-red-500/10'
                    }`}>
                      <p className="text-xs text-muted-foreground mb-1">Risk Score</p>
                      <p className={`text-3xl font-bold ${
                        anomalyData.risk_score < 30 ? 'text-emerald-400' :
                        anomalyData.risk_score < 60 ? 'text-yellow-400' : 'text-red-400'
                      }`}>{anomalyData.risk_score}</p>
                      <p className="text-xs text-muted-foreground">/100</p>
                    </div>
                    <div className="flex-1 grid grid-cols-3 gap-4">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-white">{anomalyData.statistics?.total_expenses || 0}</p>
                        <p className="text-xs text-muted-foreground">Transactions Scanned</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-white">{anomalyData.anomalies?.length || 0}</p>
                        <p className="text-xs text-muted-foreground">Anomalies Found</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-white">{formatCurrency(anomalyData.statistics?.avg_expense)}</p>
                        <p className="text-xs text-muted-foreground">Avg Transaction</p>
                      </div>
                    </div>
                  </div>

                  {/* Summary */}
                  {anomalyData.summary && (
                    <div className="bg-orbit-bg/50 rounded-xl p-4">
                      <p className="text-sm text-muted-foreground">{anomalyData.summary}</p>
                    </div>
                  )}

                  {/* Anomalies List */}
                  {anomalyData.anomalies && anomalyData.anomalies.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-white font-medium">Detected Anomalies</h4>
                      {anomalyData.anomalies.map((anomaly, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className={`p-4 rounded-xl border ${getSeverityColor(anomaly.severity)}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <AlertCircle className="w-5 h-5 mt-0.5" />
                              <div>
                                <p className="font-medium">{anomaly.type}</p>
                                <p className="text-sm opacity-80">{anomaly.description}</p>
                                {anomaly.recommendation && (
                                  <p className="text-xs mt-2 opacity-60">💡 {anomaly.recommendation}</p>
                                )}
                              </div>
                            </div>
                            <Badge className={getSeverityColor(anomaly.severity)}>
                              {anomaly.severity}
                            </Badge>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Shield className="w-12 h-12 text-red-400/50 mx-auto mb-4" />
                  <p className="text-muted-foreground">Click "Scan Now" to detect anomalies in your transactions</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Smart Invoice Tab */}
        <TabsContent value="invoice" className="space-y-6">
          <Card className="bg-gradient-to-br from-cyan-500/10 to-blue-500/5 border-cyan-500/20">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <CardTitle className="text-white">AI Smart Invoice Generator</CardTitle>
                  <p className="text-sm text-muted-foreground">Auto-fill invoices based on client history</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Client Search */}
              <div className="flex gap-3">
                <div className="flex-1">
                  <Input
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    placeholder="Enter client name (e.g., ABC Company)"
                    className="bg-orbit-bg/50 border-cyan-500/30"
                    onKeyPress={(e) => e.key === 'Enter' && fetchInvoiceSuggestions()}
                  />
                </div>
                <Button 
                  onClick={fetchInvoiceSuggestions}
                  disabled={invoiceLoading}
                  className="bg-cyan-500 hover:bg-cyan-600"
                >
                  {invoiceLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  Generate
                </Button>
              </div>

              {/* Suggestions */}
              <AnimatePresence>
                {invoiceSuggestions && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="space-y-4"
                  >
                    {/* Client Info */}
                    <div className="flex items-center justify-between bg-orbit-bg/50 rounded-xl p-4">
                      <div>
                        <p className="text-white font-medium">{invoiceSuggestions.client_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {invoiceSuggestions.has_history 
                            ? `${invoiceSuggestions.past_invoice_count} past invoices • Avg ${formatCurrency(invoiceSuggestions.avg_invoice_value)}`
                            : 'New client - no history'
                          }
                        </p>
                      </div>
                      <Badge className={invoiceSuggestions.has_history ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400'}>
                        {invoiceSuggestions.has_history ? 'Returning' : 'New'}
                      </Badge>
                    </div>

                    {/* Suggested Line Items */}
                    <div className="bg-orbit-bg/30 rounded-xl p-4">
                      <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-cyan-400" />
                        AI Suggested Line Items
                      </h4>
                      <div className="space-y-2">
                        {invoiceSuggestions.suggestions?.suggested_line_items?.map((item, index) => (
                          <div key={index} className="flex items-center justify-between py-2 border-b border-orbit-border last:border-0">
                            <div>
                              <p className="text-white text-sm">{item.description}</p>
                              <p className="text-xs text-muted-foreground">Qty: {item.quantity} × {formatCurrency(item.unit_price)} + {item.tax_rate}% tax</p>
                            </div>
                            <p className="text-cyan-400 font-medium">
                              {formatCurrency(item.quantity * item.unit_price * (1 + item.tax_rate/100))}
                            </p>
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-between items-center mt-4 pt-4 border-t border-orbit-border">
                        <span className="text-white font-medium">Suggested Total</span>
                        <span className="text-xl font-bold text-cyan-400">{formatCurrency(invoiceSuggestions.suggested_total)}</span>
                      </div>
                    </div>

                    {/* Additional Suggestions */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-orbit-bg/30 rounded-xl p-4">
                        <p className="text-xs text-muted-foreground mb-1">Payment Terms</p>
                        <p className="text-white font-medium">{invoiceSuggestions.suggestions?.payment_terms || 'Net 30'}</p>
                      </div>
                      <div className="bg-orbit-bg/30 rounded-xl p-4">
                        <p className="text-xs text-muted-foreground mb-1">Suggested Discount</p>
                        <p className="text-white font-medium">{invoiceSuggestions.suggestions?.suggested_discount || 0}%</p>
                      </div>
                    </div>

                    {/* Notes */}
                    {invoiceSuggestions.suggestions?.notes && (
                      <div className="bg-orbit-bg/30 rounded-xl p-4">
                        <p className="text-xs text-muted-foreground mb-1">Suggested Notes</p>
                        <p className="text-white text-sm">{invoiceSuggestions.suggestions.notes}</p>
                      </div>
                    )}

                    {/* Confidence */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">AI Confidence</span>
                      <span className="text-cyan-400">{Math.round((invoiceSuggestions.suggestions?.confidence || 0.8) * 100)}%</span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {!invoiceSuggestions && !invoiceLoading && (
                <div className="text-center py-8">
                  <FileCheck className="w-12 h-12 text-cyan-400/50 mx-auto mb-4" />
                  <p className="text-muted-foreground">Enter a client name to generate smart invoice suggestions</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tax Optimization Tab */}
        <TabsContent value="tax" className="space-y-6">
          <Card className="bg-gradient-to-br from-emerald-500/10 to-teal-500/5 border-emerald-500/20">
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                  <Calculator className="w-5 h-5 text-white" />
                </div>
                <div>
                  <CardTitle className="text-white">AI Tax Optimizer</CardTitle>
                  <p className="text-sm text-muted-foreground">Malaysia LHDN compliant tax suggestions</p>
                </div>
              </div>
              <Button 
                onClick={fetchTaxOptimization}
                disabled={taxLoading}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                {taxLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Analyze
              </Button>
            </CardHeader>
            <CardContent>
              {taxLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
                    <span className="text-muted-foreground">AI is analyzing your tax situation...</span>
                  </div>
                </div>
              ) : taxData ? (
                <div className="space-y-6">
                  {/* Savings Summary */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-emerald-500/10 rounded-xl p-4 text-center">
                      <p className="text-xs text-muted-foreground mb-1">Potential Deductions</p>
                      <p className="text-2xl font-bold text-emerald-400">{formatCurrency(taxData.total_potential_deductions)}</p>
                    </div>
                    <div className="bg-emerald-500/10 rounded-xl p-4 text-center">
                      <p className="text-xs text-muted-foreground mb-1">Estimated Tax Savings</p>
                      <p className="text-2xl font-bold text-emerald-400">{formatCurrency(taxData.estimated_tax_savings)}</p>
                    </div>
                    <div className="bg-primary/10 rounded-xl p-4 text-center">
                      <p className="text-xs text-muted-foreground mb-1">Current Gross Profit</p>
                      <p className="text-2xl font-bold text-primary">{formatCurrency(taxData.financial_summary?.gross_profit)}</p>
                    </div>
                  </div>

                  {/* Deductions List */}
                  {taxData.deductions && taxData.deductions.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-white font-medium flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-emerald-400" />
                        Tax Deduction Opportunities
                      </h4>
                      {taxData.deductions.map((deduction, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="bg-orbit-bg/50 rounded-xl p-4"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="text-white font-medium">{deduction.category}</p>
                              <p className="text-sm text-muted-foreground">{deduction.description}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-emerald-400 font-bold">{formatCurrency(deduction.amount)}</p>
                              <p className="text-xs text-emerald-400/70">Save ~{formatCurrency(deduction.potential_savings)}</p>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}

                  {/* Compliance Tips */}
                  {taxData.compliance_tips && taxData.compliance_tips.length > 0 && (
                    <div className="bg-orbit-bg/30 rounded-xl p-4">
                      <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-400" />
                        LHDN Compliance Tips
                      </h4>
                      <ul className="space-y-2">
                        {taxData.compliance_tips.map((tip, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <span className="text-emerald-400">•</span>
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Recommendations */}
                  {taxData.recommendations && taxData.recommendations.length > 0 && (
                    <div className="bg-primary/10 rounded-xl p-4">
                      <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-primary" />
                        AI Recommendations
                      </h4>
                      <ul className="space-y-2">
                        {taxData.recommendations.map((rec, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <span className="text-primary">→</span>
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Risk Areas */}
                  {taxData.risk_areas && taxData.risk_areas.length > 0 && (
                    <div className="bg-yellow-500/10 rounded-xl p-4">
                      <h4 className="text-yellow-400 font-medium mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Areas to Watch
                      </h4>
                      <ul className="space-y-2">
                        {taxData.risk_areas.map((area, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm text-yellow-400/80">
                            <span>⚠️</span>
                            {area}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Calculator className="w-12 h-12 text-emerald-400/50 mx-auto mb-4" />
                  <p className="text-muted-foreground">Click "Analyze" to get AI-powered tax optimization suggestions</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AITools;
